import { useState, CSSProperties, useCallback, DOMAttributes } from 'react';
import { flattenStyles, isBrowser } from 'utils';
import Observer from 'components/Shared/Observer';
import { headerConnect as connect } from 'components/Shared/connect';
import cn from 'classnames';
import styles from './styles.scss';
import { BhImage } from 'models/BhImage';
import { BhImageType } from 'models/BhImageType';
import { imageUrl } from 'utils/cdn';
import ResponsiveImageModel from './ResponsiveImage';
import { compose } from 'redux';
import { isVideo } from 'views/ProductPage/selector';
import { VideoThumbnailIcon } from 'views/ProductPage/productMedia/themes/BGH/VideoThumbnailIcon';
import { openVideo } from 'components/VideoPlayer/actions';
import { useDispatch, useSelector } from 'react-redux';
import { BhVideo } from 'models/BhVideo';
import { selectIsBot } from './selector';

type AppearanceType = 'rounded';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OverrideStyleInterface = any;
const _styles: OverrideStyleInterface = styles;
const fa = flattenStyles(_styles);

type Src = Pick<
  MergedProps,
  'type' | 'src' | 'id' | 'name' | 'cdnUrl' | 'width' | 'height' | 'isBot'
>;

const getDpr = ({ isBot }: MergedProps): DPRS => {
  if ((isBrowser() && window.devicePixelRatio > 1) || isBot) {
    return 2;
  }
  return 1;
};
export const getSrc = ({ src, ...rest }: Src, dpr: DPRS) =>
  rest.type in BhImageType ? imageUrl({ ...rest, dpr }) : src || '';

type DPRS = 1 | 2 | number;

const getSrcSet = (props: MergedProps, dpr: DPRS) => {
  return (
    props.responsive &&
    props.responsive.srcSet
      .map(({ width, height, viewWidth }) => {
        const src = `${encodeURI(getSrc({ ...props, width, height }, dpr))}`;
        const attr = `${viewWidth * dpr}w`;
        return `${src} ${attr}`;
      })
      .join(', ')
  );
};

type OwnProps = {
  src?: string;
  responsive?: ResponsiveImageModel;
  className?: string;
  wrapperClassName?: string;
  height: number;
  width: number;
  appearance?: AppearanceType;
  alt?: string;
  /**
   * Prop telling the image to display a wrapper for the image that uses
   * the padding bottom trick for taking up space before the image has been loaded. The value
   * should be a percentage
   *
   * If the value true is passed, it uses (height / width) * 100  for paddinBottom
   *
   * NOTE: If you want to use the paddingBottom prop inside a flex row container, you should wrap
   * the Image inside a div that has the appropriate witdth/flex set, since padding-bottom
   * percentage values use the width of the parent as 100%.
   *
   * Expaple
   *
   * <div style={{flex: 0 0 100px}}>
   *   <Image paddingBottom />
   * </div>
   *
   */
  paddingBottom?: boolean;
  lazy?: boolean;
  wrapperStyle?: CSSProperties;
  onClick?: React.EventHandler<React.MouseEvent>;
} & Pick<DOMAttributes<HTMLElement>, 'onClick' | 'onMouseOver' | 'onMouseOut'>;

interface MergedProps extends Image {
  cdnUrl: string;
  width: number;
  height: number;
  isBot: boolean;
}

const getSrcSetsAndSizes = (props: MergedProps, dpr: DPRS) => {
  const src = getSrc(props, dpr);
  const srcSet = (!isBrowser() || props.responsive) && getSrcSet(props, dpr);
  const sizes = props.responsive && props.responsive.sizes;

  return { srcSet, src, sizes };
};

const aspectRatio = ({ aspectRatio, height, width }: MergedProps) =>
  aspectRatio || (height / width) * 100;

const initSrcSetsAndSizes = compose(
  ({ src, srcSet, ...props }) => ({
    ...props,
    'data-src': src,
    'data-srcset': srcSet
  }),
  getSrcSetsAndSizes
);
interface Image extends BhImage, OwnProps {}
interface State {
  src?: string;
  srcSet?: string;
  ['data-src']?: string;
  ['data-srcset']?: string;
}

// Hardcoded to 1 to be identical on SSR and hydration.
// Client will calculate correct value when loading image
export const INITIAL_DPR: DPRS = 1;
const LazyImage: React.FC<MergedProps> = props => {
  const [state, setState] = useState<State>(initSrcSetsAndSizes(props, INITIAL_DPR));
  const [loaded, setLoaded] = useState(false);

  const onChange = useCallback(
    (isVisible: boolean) => {
      if (!isVisible) {
        return;
      }
      setState(getSrcSetsAndSizes(props, getDpr(props)));
    },
    [props]
  );
  const handleLoadedImage = (isVisible: boolean) => () => {
    if (isVisible) {
      setLoaded(true);
    }
  };

  const events = {
    onClick: props.onClick,
    onMouseOver: props.onMouseOver,
    onMouseOut: props.onMouseOut
  };

  if (!props.paddingBottom) {
    return (
      <Observer
        type="img"
        threshold={0}
        onChange={onChange}
        className={cn(styles.lazy, props.className, fa(props.appearance))}
        alt={props.alt}
        {...state}
        {...events}
      />
    );
  }
  const ar = aspectRatio(props);
  return (
    <Observer
      type="div"
      threshold={0}
      onChange={onChange}
      className={cn(styles.lazyWrapper, props.wrapperClassName)}
      style={{
        ...props.wrapperStyle,
        paddingBottom: `${ar}%`
      }}
      onClick={props.onClick}
      onMouseOver={props.onMouseOver}
      onMouseOut={props.onMouseOut}
    >
      {(isVisible: boolean) => (
        <img
          key={props.id}
          {...events}
          {...state}
          alt={props.alt}
          onLoad={handleLoadedImage(isVisible)}
          className={cn(
            { [styles.lazy]: !loaded },
            props.className,
            fa(props.appearance),
            styles.withPaddingBottom
          )}
        />
      )}
    </Observer>
  );
};

const Image = (props: MergedProps) => {
  const events = {
    onClick: props.onClick,
    onMouseOver: props.onMouseOver,
    onMouseOut: props.onMouseOut
  };
  const { src, srcSet, sizes } = getSrcSetsAndSizes(props, INITIAL_DPR);
  if (props.paddingBottom) {
    const ar = aspectRatio(props);
    return (
      <div
        className={styles.lazyWrapper}
        style={{
          ...props.wrapperStyle,
          paddingBottom: `${ar}%`
        }}
      >
        <img
          {...events}
          src={src}
          sizes={sizes}
          srcSet={srcSet}
          alt={props.alt}
          className={cn(props.className, fa(props.appearance), styles.withPaddingBottom)}
        />
      </div>
    );
  }

  return (
    <img
      {...events}
      src={src}
      sizes={sizes}
      srcSet={srcSet}
      alt={props.alt}
      className={cn(props.className, fa(props.appearance))}
    />
  );
};

const getThumbnailUrl = (videoId: string) => `//img.youtube.com/vi/${videoId}/0.jpg`;
export const getEmbedUrl = (videoId: string) =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&origin=https://www.bygghemma.se&rel=0`;

const Video = (props: BhVideo) => {
  const isBot = useSelector(selectIsBot);
  const dispatch = useDispatch();
  const { videoId, title } = props;
  const showVideo = () => dispatch(openVideo({ id: videoId, title }));

  return (
    <div className={cn(styles.videoFrameWrapper, styles.largeArrow)} onClick={showVideo}>
      <VideoThumbnailIcon />
      <img src={getThumbnailUrl(props.videoId)} className={styles.videoThumbnail} />
      {isBot && (
        <iframe
          allowFullScreen
          className={styles.videoFrameCollapsed}
          frameBorder="none"
          src={getEmbedUrl(videoId)}
        />
      )}
    </div>
  );
};

const picture: React.FC<MergedProps | BackgroundImage> = (props: MergedProps | BackgroundImage) => {
  const { lazy = true } = props;

  if (isBgImage(props)) {
    return <BackgroundImage {...props} />;
  }

  if (isVideo(props)) {
    return <Video {...props} />;
  }

  if (lazy) {
    return <LazyImage {...props} />;
  }

  return <Image {...props} />;
};

interface BackgroundImage extends OwnProps {
  src: string;
  unit?: string;
}

const isBgImage = (props: OwnProps | Image): props is BackgroundImage =>
  (props as BackgroundImage).src !== undefined && props.paddingBottom === undefined;

const BackgroundImage = ({
  src,
  unit = 'px',
  className,
  height,
  width,
  appearance,
  onClick,
  onMouseOut,
  onMouseOver
}: BackgroundImage) => (
  <div
    onClick={onClick}
    onMouseOut={onMouseOut}
    onMouseOver={onMouseOver}
    className={cn(styles.background, className, fa(appearance))}
    style={{
      height: `${height}px`,
      width: `${width}${unit}`,
      backgroundImage: `url(${src})`
    }}
  />
);

export default connect<MergedProps, OwnProps & Partial<BhImage>>(
  ({
    appShell: {
      links: { cdnUrl },
      isBot
    }
  }) => ({
    isBot,
    cdnUrl
  })
)(picture);
